import React, { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SiteFooter from "../common/SiteFooter";

type Props = {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
};

/** Shared chrome for the Terms / Privacy / Refund pages. */
const LegalPageLayout: React.FC<Props> = ({ title, lastUpdated, children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-[#F7F3EF] text-[#1B1511]">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F7F3EF] via-[#F0EAE4] to-[#E8E0D8]" />
        <div className="relative mx-auto w-full max-w-3xl px-4 pb-10 pt-10 md:px-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-6 inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/15 bg-white/70 text-[#1B1511] backdrop-blur-sm transition hover:border-black/30"
            aria-label="Go back"
            title="Go back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h1 className="font-['Fraunces'] text-4xl font-semibold tracking-tight md:text-5xl">
            {title}
          </h1>
          <p className="mt-3 font-['Manrope'] text-sm text-black/55">
            Last updated: {lastUpdated}
          </p>
        </div>
      </div>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 pb-20 md:px-8">
        <div className="space-y-8 font-['Manrope'] text-[15px] leading-relaxed text-black/75">
          {children}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

/** Section heading + body used by every legal page. */
export const LegalSection: React.FC<{
  heading: string;
  children: React.ReactNode;
}> = ({ heading, children }) => (
  <section className="space-y-3">
    <h2 className="font-['Fraunces'] text-xl font-semibold text-[#1B1511]">
      {heading}
    </h2>
    {children}
  </section>
);

export const LegalList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="ml-1 space-y-2">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1B1511]/40" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default LegalPageLayout;
